import React, { useEffect, useMemo, useState } from "react";
import {
    RotateCw,
    Settings2,
    ShieldCheck,
    CalendarDays,
    Clock,
    Briefcase,
    Receipt,
    Coins,
    Gift,
    FileCheck2,
    ChevronDown,
    ChevronRight,
    CheckCheck,
    UserCheck,
    Save,
} from "lucide-react";
import "../parametresStyles/WorkflowManagement.css";
import { useDispatch, useSelector } from "react-redux";
import {
    getWorkflowLevelsRequest,
    saveWorkflowLevelsRequest,
} from "../../../../redux/actions/workflowValidationActions";

/* ── Données ─────────────────────────────────────────────── */
const GROUPS = [
    {
        id: "conges",
        label: "Congés & absences",
        Icon: CalendarDays,
        items: [
            {
                id: "conge",
                title: "Congés & absences",
                Icon: CalendarDays,
                description: "Validation des demandes de congés, maladie et absences spéciales.",
            },
            {
                id: "feuille_temps",
                title: "Feuilles de temps",
                Icon: Clock,
                description: "Validation des feuilles de temps hebdomadaires ou mensuelles.",
            },
        ],
    },
    {
        id: "finance",
        label: "Finance & déplacements",
        Icon: Briefcase,
        items: [
            {
                id: "mission",
                title: "Ordres de mission",
                Icon: Briefcase,
                description: "Validation des demandes de mission saisies par les collaborateurs.",
            },
            {
                id: "note_frais",
                title: "Notes de frais",
                Icon: Receipt,
                description: "Validation des notes de frais et remboursements.",
            },
            {
                id: "avance",
                title: "Avances & prêts",
                Icon: Coins,
                description: "Validation des demandes d'avance et de prêt.",
            },
            {
                id: "avantage_social",
                title: "Avantages sociaux",
                Icon: Gift,
                description: "Validation des demandes d'avantages et primes des collaborateurs.",
            },
        ],
    },
    {
        id: "documents",
        label: "Documents RH",
        Icon: FileCheck2,
        items: [
            {
                id: "attestation",
                title: "Attestations & documents",
                Icon: FileCheck2,
                description: "Validation des attestations et documents RH.",
            },
        ],
    },
];

const ALL_ITEMS = GROUPS.flatMap((g) => g.items);

const FILTERS = [
    { key: "all", label: "Tous" },
    { key: "both", label: "Double" },
    { key: "manager", label: "Manager" },
    { key: "admin", label: "RH uniquement" },
];

/* ── Helpers ─────────────────────────────────────────────── */
function badgeLabel(v) {
    if (v === "both") return "Double validation";
    if (v === "manager") return "Manager uniquement";
    return "RH uniquement";
}

function BadgeIcon({ value, size = 11 }) {
    if (value === "both") return <CheckCheck size={size} />;
    if (value === "manager") return <UserCheck size={size} />;
    return <ShieldCheck size={size} />;
}

/* ── WorkflowCard ────────────────────────────────────────── */
function WorkflowCard({ item, value, onChange, disabled }) {
    const { title, Icon, description } = item;
    const managerOn = value === "both" || value === "manager";
    const adminOn = value === "both" || value === "admin";

    const handlePill = (key, checked) => {
        const newManager = key === "manager" ? checked : managerOn;
        const newAdmin = key === "admin" ? checked : adminOn;
        if (!newManager && !newAdmin) return;
        onChange(newManager && newAdmin ? "both" : newManager ? "manager" : "admin");
    };

    return (
        <div className="wf-card">
            <div className="wf-card-title">
                <Icon size={14} />
                {title}
            </div>
            <div className="wf-card-desc">{description}</div>
            <div className="wf-pills">
                <label className={`wf-pill${managerOn ? " active-manager" : ""}`}>
                    <input
                        type="checkbox"
                        style={{ display: "none" }}
                        checked={managerOn}
                        disabled={disabled}
                        onChange={(e) => handlePill("manager", e.target.checked)}
                    />
                    <span className="wf-pill-dot" />
                    Manager
                </label>
                <label className={`wf-pill${adminOn ? " active-admin" : ""}`}>
                    <input
                        type="checkbox"
                        style={{ display: "none" }}
                        checked={adminOn}
                        disabled={disabled}
                        onChange={(e) => handlePill("admin", e.target.checked)}
                    />
                    <span className="wf-pill-dot" />
                    Resp. RH
                </label>
            </div>
            <span className="wf-badge">
                <BadgeIcon value={value} />
                {badgeLabel(value)}
            </span>
        </div>
    );
}

/* ── WorkflowGroup ───────────────────────────────────────── */
function WorkflowGroup({ group, levels, onLevelChange, disabled, filter }) {
    const [open, setOpen] = useState(true);

    const visibleItems = useMemo(
        () =>
            group.items.filter((item) => {
                if (filter === "all") return true;
                return levels[item.id] === filter;
            }),
        [group.items, levels, filter]
    );

    if (visibleItems.length === 0) return null;

    const { Icon } = group;

    return (
        <div className="wf-group">
            <button
                type="button"
                className="wf-group-header"
                onClick={() => setOpen((o) => !o)}
            >
                <div className="wf-group-left">
                    <Icon size={15} className="wf-group-icon" />
                    <span className="wf-group-name">{group.label}</span>
                    <span className="wf-group-count">
                        {visibleItems.length} workflow{visibleItems.length > 1 ? "s" : ""}
                    </span>
                </div>
                <span className="wf-group-chevron">
                    {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
            </button>

            {open && (
                <div className="wf-group-body">
                    <div className="wf-cards">
                        {visibleItems.map((item) => (
                            <WorkflowCard
                                key={item.id}
                                item={item}
                                value={levels[item.id] || "both"}
                                onChange={(val) => onLevelChange(item.id, val)}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Main ────────────────────────────────────────────────── */
export default function WorkflowManagement() {
    const dispatch = useDispatch();

    const loading = useSelector((state) => state.workflowValidation?.loading);
    const levelsFromStore = useSelector((state) => state.workflowValidation?.levels || {});
    const error = useSelector((state) => state.workflowValidation?.error);
    const lastSavedAt = useSelector((state) => state.workflowValidation?.lastSavedAt);

    const [levels, setLevels] = useState({});
    const [activeFilter, setActiveFilter] = useState("all");
    const [activeTab, setActiveTab] = useState("config");

    /* Init levels depuis le store */
    useEffect(() => {
        const defaults = ALL_ITEMS.reduce((acc, item) => {
            acc[item.id] = levelsFromStore[item.id] || "both";
            return acc;
        }, {});
        setLevels(defaults);
    }, [levelsFromStore]);

    useEffect(() => {
        dispatch(getWorkflowLevelsRequest());
    }, [dispatch]);

    const handleLevelChange = (id, val) => {
        setLevels((prev) => ({ ...prev, [id]: val }));
    };

    const handleRefresh = () => {
        dispatch(getWorkflowLevelsRequest());
    };

    const handleSave = () => {
        const payload = ALL_ITEMS.reduce((acc, item) => {
            acc[item.id] = levels[item.id] || "both";
            return acc;
        }, {});
        dispatch(saveWorkflowLevelsRequest(payload));
    };

    const isFormValid = useMemo(
        () => ALL_ITEMS.every((item) => !!levels[item.id]),
        [levels]
    );

    const visibleCount = useMemo(
        () =>
            ALL_ITEMS.filter((item) => {
                if (activeFilter === "all") return true;
                return levels[item.id] === activeFilter;
            }).length,
        [levels, activeFilter]
    );

    return (
        <div className="wf-page">
            {/* ── Tabs ── */}
            {/*<div className="wf-tabs">*/}
            {/*    <button*/}
            {/*        type="button"*/}
            {/*        className={`wf-tab${activeTab === "config" ? " active" : ""}`}*/}
            {/*        onClick={() => setActiveTab("config")}*/}
            {/*    >*/}
            {/*        Configuration des workflows*/}
            {/*    </button>*/}
            {/*    <button*/}
            {/*        type="button"*/}
            {/*        className={`wf-tab${activeTab === "history" ? " active" : ""}`}*/}
            {/*        onClick={() => setActiveTab("history")}*/}
            {/*    >*/}
            {/*        Historique*/}
            {/*    </button>*/}
            {/*</div>*/}

            {activeTab === "config" && (
                <>
                    {/* ── Header ── */}
                    <div className="wf-header">
                        <div className="wf-header-left">
                            <h2>
                                <Settings2 size={17} />
                                Configuration des workflows
                            </h2>
                            <p className="wf-subtitle">
                                Choisissez le circuit de validation utilisé par les demandes RH.
                            </p>
                            {lastSavedAt && (
                                <div className="wf-saved-at">
                                    <Clock size={11} />
                                    Dernier enregistrement :{" "}
                                    {new Date(lastSavedAt).toLocaleString("fr-FR")}
                                </div>
                            )}
                        </div>
                        <div className="wf-header-actions">
                            <button
                                type="button"
                                className="wf-btn-icon"
                                onClick={handleRefresh}
                                disabled={!!loading}
                                aria-label="Rafraîchir"
                                title="Rafraîchir"
                            >
                                <RotateCw size={15} />
                            </button>
                            <button
                                type="button"
                                className="wf-btn wf-btn-primary"
                                onClick={handleSave}
                                disabled={!!loading || !isFormValid}
                            >
                                <Save size={14} />
                                Enregistrer
                            </button>
                        </div>
                    </div>

                    {/* ── Toolbar ── */}
                    <div className="wf-toolbar">
                        <span className="wf-filter-label">Validation</span>
                        <div className="wf-filter-tabs">
                            {FILTERS.map((f) => (
                                <button
                                    key={f.key}
                                    type="button"
                                    className={`wf-filter-tab${activeFilter === f.key ? " active" : ""}`}
                                    onClick={() => setActiveFilter(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <span className="wf-meta">
                            {loading
                                ? "Chargement..."
                                : `${visibleCount} workflow${visibleCount !== 1 ? "s" : ""}`}
                        </span>
                    </div>

                    {/* ── Alerts ── */}
                    {error && <div className="wf-alert wf-alert-error">{error}</div>}

                    {/* ── Content ── */}
                    {loading ? (
                        <div className="wf-loading">
                            <div className="wf-spinner" />
                            <span>Chargement...</span>
                        </div>
                    ) : (
                        <div className="wf-groups">
                            {visibleCount === 0 ? (
                                <div className="wf-empty">Aucun workflow ne correspond au filtre.</div>
                            ) : (
                                GROUPS.map((group) => (
                                    <WorkflowGroup
                                        key={group.id}
                                        group={group}
                                        levels={levels}
                                        onLevelChange={handleLevelChange}
                                        disabled={!!loading}
                                        filter={activeFilter}
                                    />
                                ))
                            )}
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div className="wf-footer">
                        <button
                            type="button"
                            className="wf-btn wf-btn-outline"
                            onClick={handleRefresh}
                            disabled={!!loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            className="wf-btn wf-btn-primary"
                            onClick={handleSave}
                            disabled={!!loading || !isFormValid}
                        >
                            <Save size={14} />
                            Enregistrer les workflows
                        </button>
                    </div>
                </>
            )}

            {activeTab === "history" && (
                <div className="wf-empty" style={{ marginTop: 40 }}>
                    Historique des modifications — à venir.
                </div>
            )}
        </div>
    );
}
